import { useState, useEffect, useCallback } from "react";
import { Package } from "lucide-react";

/**
 * ProductImage component with error handling and retry logic
 * Handles unreliable external image URLs (like Squarespace CDN)
 */
export default function ProductImage({ 
  src, 
  alt = "", 
  className = "", 
  fallbackClassName = "",
  containerClassName = "",
  showFallbackIcon = true,
  retryCount = 3,
  retryDelay = 1500
}) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  // Reset state when src prop changes
  useEffect(() => {
    if (src) {
      setCurrentSrc(src);
      setHasError(false);
      setIsLoading(true);
      setAttempts(0);
    }
  }, [src]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleError = useCallback(() => {
    if (attempts < retryCount && src) {
      // Retry with cache-busting parameter
      const timer = setTimeout(() => {
        setAttempts(prev => prev + 1);
        const separator = src.includes('?') ? '&' : '?';
        const newSrc = src + separator + '_t=' + Date.now();
        setCurrentSrc(newSrc);
      }, retryDelay);
      return () => clearTimeout(timer);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  }, [attempts, retryCount, retryDelay, src]);

  // No source provided
  if (!src) {
    return (
      <div className={containerClassName || "w-full h-full flex items-center justify-center bg-[#121212]"}>
        {showFallbackIcon && <Package className={fallbackClassName || "w-16 h-16 text-[#3f3f46]"} />}
      </div>
    );
  }

  // Error state - show fallback
  if (hasError) {
    return (
      <div className={containerClassName || "w-full h-full flex items-center justify-center bg-[#121212]"}>
        {showFallbackIcon && <Package className={fallbackClassName || "w-16 h-16 text-[#3f3f46]"} />}
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1c1c1c] z-10">
          <div className="w-6 h-6 border-2 border-[#C0A062] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <img
        src={currentSrc}
        alt={alt}
        className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        style={{ transition: 'opacity 0.3s ease-in-out' }}
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
